/**
 * The client-safe half of a person's Incentives tab: the shape the API returns,
 * plus the pure formatting and arithmetic the screen needs over it.
 *
 * Separate from `src/api/person-incentive.ts` for the same hard reason
 * `person-leave.ts` is separate from `person-pto.ts`: that module reaches the
 * gateway and imports the logger, and a component importing a VALUE from it drags
 * `process` into the browser bundle and blanks the app. Types are erased and
 * harmless; anything needed at runtime belongs here.
 *
 * Everything in these types is a PERCENTAGE or a KPI result. There is no pay or
 * bonus amount, here or in the NOS reads behind it, and nothing here should grow
 * one. (A KPI such as Net Revenue is itself a dollar figure — that is the
 * company's number, not the person's.)
 */

/** How a KPI's numbers are read — NOS's own metric format, collapsed. */
export type IncentiveValueFormat = "currency" | "number" | "percent";

/** Which of the two NOS1 tables a KPI belongs in. */
export type IncentiveKpiGroup = "company" | "personal";

/** One KPI row. Percent-format values are fractions (0.03 = 3%). */
export interface IncentiveKpi {
  /** Stable list key. */
  key: string;
  name: string;
  /** What the KPI measures, in NOS's words; null when NOS holds none. */
  definition: string | null;
  group: IncentiveKpiGroup;
  /** The org structure it is scored under, e.g. "Nymbl" or "Solution Architects". */
  scope: string | null;
  /** Pass/fail rather than a sliding scale — shown as Met / Not met. */
  isBinary: boolean;
  format: IncentiveValueFormat;
  /** Decimal places the metric is displayed with (currency and numbers). */
  decimals: number;
  /** Share of the award this KPI carries (fraction). */
  weight: number | null;
  target: number | null;
  /** The band the award scales across; null for a KPI with no band. */
  min: number | null;
  max: number | null;
  actual: number | null;
  /** NOS's own rendering of `actual`; preferred over re-formatting it. */
  actualDisplay: string | null;
  projected: number | null;
  projectedDisplay: string | null;
  /** How far the actual is toward target, as a fraction (1.435 = 143.5%). */
  achieved: number | null;
  achievedProjected: number | null;
  /** The part of `weight` earned so far (fraction of the award). */
  awarded: number | null;
  awardedProjected: number | null;
  adjustment: number | null;
}

/** A reduction taken off the base award. */
export interface IncentiveModifier {
  /** "Late Time Entry", "Profit Safeguard" or "Utilization". */
  type: string;
  /** Percentage points subtracted from the base award, as a fraction. */
  points: number;
}

/** The year-level award, as percentages. */
export interface IncentiveAwards {
  company: number | null;
  individual: number | null;
  base: number | null;
  final: number | null;
}

export interface PersonIncentive {
  /** False when the server has no gateway credentials — not an error. */
  connected: boolean;
  year: number;
  /** False when NOS holds no scorecard for this person this year. */
  hasScorecard: boolean;
  awards: IncentiveAwards | null;
  modifiers: IncentiveModifier[];
  /**
   * True when the modifiers read failed while the scorecard loaded, so "no
   * reductions" cannot be told apart from "could not check".
   */
  modifiersUnavailable: boolean;
  kpis: IncentiveKpi[];
}

/**
 * How far through `year` the given moment is, as a fraction clamped to 0–1.
 *
 * NOS stores no such figure, so it is derived: whole days elapsed (counting today
 * as elapsed) over days in the year. A past year is complete and a future one has
 * not begun.
 */
export function yearCompleteFraction(year: number, now: Date = new Date()): number {
  const start = Date.UTC(year, 0, 1);
  const end = Date.UTC(year + 1, 0, 1);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const elapsed = (today - start) / 86_400_000 + 1;
  const total = (end - start) / 86_400_000;
  return Math.min(1, Math.max(0, elapsed / total));
}

/** `0.3745` → `"37.45%"`; trailing zeros are dropped, so `0.3` → `"30%"`. */
export function formatPercent(fraction: number | null, maxDecimals = 2): string {
  if (fraction === null) return "—";
  const pct = fraction * 100;
  // toFixed then Number() trims the trailing zeros without scientific notation.
  return `${Number(pct.toFixed(maxDecimals)).toLocaleString("en-US")}%`;
}

/**
 * A KPI value in its own units. `display` is NOS's rendering and wins when
 * present; otherwise the number is formatted from the metric's format.
 */
export function formatKpiValue(
  value: number | null,
  kpi: Pick<IncentiveKpi, "format" | "decimals">,
  display: string | null = null,
): string {
  if (display !== null && display.trim() !== "") return display;
  if (value === null) return "—";
  if (kpi.format === "percent") return formatPercent(value, 1);
  const body = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: kpi.decimals,
    maximumFractionDigits: kpi.decimals,
  });
  const unit = kpi.format === "currency" ? `$${body}` : body;
  // Negatives in brackets, as NOS's own formats do.
  return value < 0 ? `(${unit})` : unit;
}

/** The whole award less its reductions: what the modifiers add up to. */
export function totalReduction(modifiers: IncentiveModifier[]): number {
  return modifiers.reduce((sum, m) => sum + m.points, 0);
}

/**
 * How much of its weight a KPI has earned, 0–1, for the progress bar. Null when
 * there is no weight to be a share of.
 */
export function earnedShare(kpi: IncentiveKpi): number | null {
  if (kpi.weight === null || kpi.weight <= 0 || kpi.awarded === null) return null;
  return Math.min(1, Math.max(0, kpi.awarded / kpi.weight));
}
